import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'utils/toast';

import {
  FARMS_PAGE_PATH,
  FARMS_QUERY_KEY,
  FARMS_URL_PARAM_GROUP_BY,
  FARMS_URL_PARAM_YEAR,
  INPUTS_QUERY_KEY,
  ORGANISATION_LOAD_ERROR_MESSAGE,
  ZONES_QUERY_KEY,
} from '../constants/Constant';
import { InputType } from '../interfaces/IInput';
import {
  setActiveOrganisationId,
  setOrganisationSwitching,
} from '../redux/slices/ActiveOrganisationSlice';
import { clearFarmSelection } from '../redux/slices/FarmSlice';
import { clearInputSelection } from '../redux/slices/InputSlice';
import { resetInsights } from '../redux/slices/InsightsSlice';
import { AppDispatch } from '../redux/Store';
import FarmService from '../services/FarmService';
import InputService from '../services/InputService';
import ZoneService from '../services/ZoneService';

/**
 * Custom hook for handling organization opening flow
 * Manages API calls and error handling to maintain active org state properly
 */
const useOpenOrganisation = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  /**
   * Opens an organization with proper error handling to maintain the active org state
   * @param organisationId - The ID of the organization to open
   * @param skipNavigation - Optional flag to skip navigation to farms page (useful when already there)
   */
  const openOrganisation = useCallback(
    async (organisationId: string, skipNavigation = false) => {
      if (!organisationId) return;

      dispatch(setOrganisationSwitching(true));
      dispatch(clearFarmSelection());
      dispatch(clearInputSelection());
      dispatch(resetInsights());

      try {
        await Promise.all([
          queryClient.fetchQuery({
            queryKey: [FARMS_QUERY_KEY, organisationId],
            queryFn: () => FarmService.GetByOrgActive(organisationId),
            staleTime: 1000 * 60 * 5,
          }),
          queryClient.fetchQuery({
            queryKey: [INPUTS_QUERY_KEY, organisationId],
            queryFn: () =>
              InputService.GetByOrgActive(organisationId, InputType.PlanActual),
            staleTime: 1000 * 60 * 5,
          }),
          queryClient.fetchQuery({
            queryKey: [ZONES_QUERY_KEY, organisationId],
            queryFn: () => ZoneService.GetByOrgActive(organisationId),
            staleTime: 1000 * 60 * 5,
          }),
        ]);

        dispatch(setActiveOrganisationId(organisationId));

        if (!skipNavigation) {
          // groupBy is a durable view preference, not tied to any one org's
          // selection (see clearFarmSelection) — carry it forward across the
          // switch. node/tab/lens are intentionally dropped: they're reset to
          // their defaults by clearFarmSelection/resetInsights above anyway.
          // groupBy and year are durable view preferences — carry them forward
          // across the org switch. node/tab/lens are intentionally dropped.
          const groupBy = searchParams.get(FARMS_URL_PARAM_GROUP_BY);
          const year = searchParams.get(FARMS_URL_PARAM_YEAR);
          const params = new URLSearchParams();
          if (groupBy) params.set(FARMS_URL_PARAM_GROUP_BY, groupBy);
          if (year) params.set(FARMS_URL_PARAM_YEAR, year);
          const query = params.toString();
          navigate(query ? `${FARMS_PAGE_PATH}?${query}` : FARMS_PAGE_PATH);
        }
      } catch (error) {
        console.error('Error opening organisation:', error);
        toast.error(ORGANISATION_LOAD_ERROR_MESSAGE);
      } finally {
        dispatch(setOrganisationSwitching(false));
      }
    },
    [dispatch, navigate, queryClient, searchParams]
  );

  return { openOrganisation };
};

export default useOpenOrganisation;
